import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class SalesEntryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('SalesEntry Error:', error, errorInfo);
    
    // Log to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      // Send error to monitoring service
      // analytics.track('SalesEntry_Error', { error: error.message, stack: error.stack });
    }
    
    this.setState({ error, errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Card className="border-danger">
          <Card.Header className="bg-danger text-white">
            <h5 className="mb-0">
              <i className="bx bx-error-circle me-2"></i>
              Sales Entry Error
            </h5>
          </Card.Header>
          <Card.Body>
            <Alert variant="danger">
              <Alert.Heading>Something went wrong</Alert.Heading>
              <p>
                An error occurred while processing your sales entry. This could be due to:
              </p>
              <ul>
                <li>Network connectivity issues</li>
                <li>Invalid product data</li>
                <li>Calculation service errors</li>
                <li>Browser compatibility issues</li>
              </ul>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-3">
                  <summary>Error Details (Development Only)</summary>
                  <pre className="mt-2 p-2 bg-light border rounded">
                    {this.state.error.message}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
              
              <div className="mt-3">
                <Button 
                  variant="primary" 
                  onClick={this.handleRetry}
                  className="me-2"
                >
                  <i className="bx bx-refresh me-1"></i>
                  Try Again
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => window.location.reload()}
                >
                  <i className="bx bx-reload me-1"></i>
                  Reload Page
                </Button>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default SalesEntryErrorBoundary;